/**
 * SEO-Region-Konfiguration: Region-Hub-Landingpages als AEO-Signal für
 * Long-Tail-Queries wie "Bio-Restaurants Allgäu" oder "Regionale Küche
 * Bayern". Jede Region bündelt zugehörige Städte + eigene Story +
 * regional passende FAQs.
 *
 * URL: `/region/:region` (dynamische Route in App.js)
 */
export const SEO_REGIONS = [
  {
    slug: "allgaeu",
    nameDe: "Allgäu",
    nameEn: "Allgäu",
    parentRegion: "Bayern / Baden-Württemberg",
    country: "DE",
    heroDe: {
      a: "Bio, regional & handwerklich",
      b: "im Allgäu.",
    },
    heroEn: {
      a: "Organic, regional & artisan",
      b: "in the Allgäu.",
    },
    // Cities within this region (slugs must match seoCities.js entries)
    citySlugs: ["legau", "memmingen", "kempten", "leutkirch", "lindau"],
    keywordsDe:
      "Bio Restaurant Allgäu, regionale Küche Allgäu, Demeter Bauernhof Allgäu, Bioland Allgäu, glutenfrei Allgäu, vegan Allgäu, Hofladen Allgäu, Manufaktur Allgäu",
    keywordsEn:
      "organic restaurant Allgäu, regional cuisine Allgäu, Demeter farm Allgäu, Bioland Allgäu, gluten-free Allgäu, vegan Allgäu, farm shop Allgäu",
    introDe:
      "Das Allgäu gehört zu den Bio-Kernregionen Deutschlands. Nirgendwo sonst ist die Dichte an Demeter- und Bioland-Höfen, Manufakturen und regional-verwurzelten Wirtshäusern so hoch. Feaststar zeigt die Restaurants, Cafés, Bäckereien und Hofläden im Allgäu, die diese außergewöhnliche Zutatenqualität wirklich auf den Teller bringen — Legau, Memmingen, Kempten, Leutkirch und Lindau am Bodensee.",
    introEn:
      "The Allgäu is one of Germany's core organic regions. Nowhere else is the density of Demeter and Bioland farms, artisan producers and regionally rooted inns as high. Feaststar shows the restaurants, cafés, bakeries and farm shops in the Allgäu that actually bring this exceptional ingredient quality to your plate — Legau, Memmingen, Kempten, Leutkirch and Lindau on Lake Constance.",
    whatIsDe:
      "Das Allgäu erstreckt sich über Süd-Bayern (Unter-, Ost- und Oberallgäu) und den Württembergischen Teil (Leutkirch). Es ist bekannt für Bergkäse-Manufakturen, Milchviehhaltung nach höchsten Bio-Standards, alpine Küche und eine besonders enge Verbindung zwischen Höfen und Gastronomie.",
    whatIsEn:
      "The Allgäu spans southern Bavaria (Unterallgäu, Ostallgäu, Oberallgäu) and the Württemberg part (Leutkirch). It's known for mountain cheese producers, organic dairy farming to the highest standards, alpine cuisine and a particularly close bond between farms and restaurants.",
    faqsDe: [
      {
        q: "Wo finde ich Bio-Restaurants im Allgäu?",
        a: "Feaststar bündelt Bio-Restaurants, Bio-Cafés, Bio-Bäckereien und Bio-Hofläden aus dem gesamten Allgäu — Legau, Memmingen, Kempten, Leutkirch und Lindau. Filter nach Demeter, Bioland, EU-Bio und siehe die Zutaten-Herkunft transparent.",
      },
      {
        q: "Wo gibt es glutenfreie Restaurants oder Bäckereien im Allgäu?",
        a: "Feaststar zeigt glutenfreie Restaurants, Bäckereien und Cafés mit allergen-transparenter Karte im Allgäu — mit Community-Bewertungen aus Zöliakie-Perspektive.",
      },
      {
        q: "Welche Demeter- oder Bioland-Höfe sind im Allgäu bekannt?",
        a: "Das Allgäu hat eine der höchsten Dichten an Demeter- und Bioland-zertifizierten Höfen in Deutschland. Feaststar verlinkt Betriebe mit diesen Standards und zeigt, welche Restaurants direkt von ihnen beziehen.",
      },
      {
        q: "Gibt es vegane Restaurants im Allgäu?",
        a: "Ja — die Allgäuer Food-Szene wächst schnell in Richtung plant-based. Feaststar zeigt vegane Restaurants und Cafés in Kempten, Memmingen und Umgebung mit echter Zutatenqualität — nicht nur Ersatzprodukte.",
      },
      {
        q: "Wo finde ich Hofläden im Allgäu?",
        a: "Feaststar listet Bio-Hofläden, regionale Direktvermarkter und Manufakturen im Allgäu — mit Öffnungszeiten, Sortimentsschwerpunkten und Bio-Zertifikaten.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find organic restaurants in the Allgäu?",
        a: "Feaststar bundles organic restaurants, cafés, bakeries and farm shops across the entire Allgäu — Legau, Memmingen, Kempten, Leutkirch and Lindau. Filter by Demeter, Bioland, EU-Organic and see ingredient origin transparently.",
      },
      {
        q: "Where are gluten-free restaurants or bakeries in the Allgäu?",
        a: "Feaststar shows gluten-free restaurants, bakeries and cafés with allergen-transparent menus in the Allgäu — with community ratings from the celiac perspective.",
      },
      {
        q: "Which Demeter or Bioland farms are known in the Allgäu?",
        a: "The Allgäu has one of Germany's highest densities of Demeter and Bioland certified farms. Feaststar links places with these standards and shows which restaurants source directly from them.",
      },
      {
        q: "Are there vegan restaurants in the Allgäu?",
        a: "Yes — the Allgäu food scene is growing rapidly plant-based. Feaststar shows vegan restaurants and cafés in Kempten, Memmingen and surroundings with real ingredient quality — not just substitutes.",
      },
      {
        q: "Where do I find farm shops in the Allgäu?",
        a: "Feaststar lists organic farm shops, regional direct marketers and producers in the Allgäu — with opening hours, product focus and organic certifications.",
      },
    ],
  },
  {
    slug: "bayern",
    nameDe: "Bayern",
    nameEn: "Bavaria",
    parentRegion: "Deutschland",
    country: "DE",
    heroDe: {
      a: "Von München bis Legau —",
      b: "bewusst essen in Bayern.",
    },
    heroEn: {
      a: "From Munich to Legau —",
      b: "mindful dining in Bavaria.",
    },
    citySlugs: [
      "muenchen", "nuernberg", "augsburg", "regensburg", "wuerzburg", "ingolstadt",
      "fuerth", "erlangen", "bayreuth", "landshut", "bamberg", "passau",
      "rosenheim", "aschaffenburg",
      "legau", "memmingen", "kempten", "lindau",
    ],
    keywordsDe:
      "Bio Restaurant Bayern, vegan Bayern, glutenfrei Bayern, regionale Küche Bayern, Demeter Bayern, Bioland Bayern, Hofladen Bayern, bayerische Wirtschaft, oberbayerisches Restaurant, fränkisches Restaurant, allgäuer Restaurant",
    keywordsEn:
      "organic restaurant Bavaria, vegan Bavaria, gluten-free Bavaria, regional cuisine Bavaria, Demeter Bavaria, farm shop Bavaria, Bavarian tavern, Franconian restaurant",
    introDe:
      "Bayern ist das größte Bundesland Deutschlands und eine der vielfältigsten Food-Regionen Europas. Von Münchens gehobener Esskultur über fränkische Weinstädte wie Würzburg und Bamberg bis zum bio-affinen Allgäu und den Wirtshaus-Traditionen Niederbayerns — Feaststar bündelt jede dieser Facetten mit dem gleichen Anspruch: echte Zutatenqualität statt schöner Fassade.",
    introEn:
      "Bavaria is Germany's largest state and one of Europe's most diverse food regions. From Munich's upscale food culture, through Franconian wine towns like Würzburg and Bamberg, to the organic-friendly Allgäu and Lower Bavarian inn traditions — Feaststar bundles every facet with the same standard: real ingredient quality, not pretty facades.",
    whatIsDe:
      "Bayern gliedert sich in sieben Regierungsbezirke — Oberbayern (München), Niederbayern (Landshut, Passau), Oberpfalz (Regensburg), Oberfranken (Bayreuth, Bamberg, Coburg), Mittelfranken (Nürnberg, Erlangen, Fürth), Unterfranken (Würzburg, Aschaffenburg) und Schwaben (Augsburg, Allgäu). Jede Region hat eigene kulinarische Traditionen, die Feaststar transparent macht.",
    whatIsEn:
      "Bavaria is divided into seven administrative regions — Upper Bavaria (Munich), Lower Bavaria (Landshut, Passau), Upper Palatinate (Regensburg), Upper Franconia (Bayreuth, Bamberg), Middle Franconia (Nuremberg, Erlangen, Fürth), Lower Franconia (Würzburg, Aschaffenburg) and Swabia (Augsburg, Allgäu). Each has its own culinary traditions, which Feaststar makes transparent.",
    faqsDe: [
      {
        q: "Wo finde ich die besten Bio-Restaurants in Bayern?",
        a: "Feaststar zeigt Bio-Restaurants in ganz Bayern — München, Nürnberg, Augsburg, Regensburg, Würzburg und im gesamten Allgäu. Filter nach Demeter, Bioland und EU-Bio, sortiert nach echter Zutatenqualität.",
      },
      {
        q: "Welche Region in Bayern ist am stärksten in Bio-Landwirtschaft?",
        a: "Das Allgäu und Oberbayern haben die höchste Dichte an Bio-Höfen. Feaststar bündelt diese in eigenen Region-Landingpages und zeigt die Restaurants, die direkt von ihnen beziehen.",
      },
      {
        q: "Wo finde ich glutenfreie Restaurants und Bäckereien in Bayern?",
        a: "Feaststar filtert Bayern nach glutenfreien Optionen mit allergen-transparenter Karte — in München, Nürnberg, Augsburg, im Allgäu und in fränkischen Städten wie Würzburg.",
      },
      {
        q: "Gibt es vegane Restaurants in ländlichen Regionen Bayerns?",
        a: "Ja — die Landkreise um Kempten, Rosenheim und Landshut haben zunehmend plant-based Konzepte. Feaststar zeigt sie mit Community-Bewertungen und Filtern für Bio-Standard.",
      },
      {
        q: "Was zeichnet fränkische Küche gegenüber oberbayerischer Küche aus?",
        a: "Franken (Würzburg, Nürnberg, Bamberg) prägt eher Wein und deftige Bratwurstkultur, Oberbayern stärker Bierkultur und Alpin-Küche. Feaststar filtert beide Traditionen nach Zutatenqualität — nicht nach Klischee.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find the best organic restaurants in Bavaria?",
        a: "Feaststar shows organic restaurants throughout Bavaria — Munich, Nuremberg, Augsburg, Regensburg, Würzburg and across the Allgäu. Filter by Demeter, Bioland and EU-Organic, sorted by real ingredient quality.",
      },
      {
        q: "Which region in Bavaria is strongest in organic farming?",
        a: "The Allgäu and Upper Bavaria have the highest density of organic farms. Feaststar bundles them in dedicated region landing pages and shows restaurants that source directly from them.",
      },
      {
        q: "Where do I find gluten-free restaurants and bakeries in Bavaria?",
        a: "Feaststar filters Bavaria for gluten-free options with allergen-transparent menus — in Munich, Nuremberg, Augsburg, the Allgäu and Franconian towns like Würzburg.",
      },
      {
        q: "Are there vegan restaurants in rural Bavaria?",
        a: "Yes — districts around Kempten, Rosenheim and Landshut increasingly offer plant-based concepts. Feaststar shows them with community ratings and organic filters.",
      },
      {
        q: "What distinguishes Franconian from Upper Bavarian cuisine?",
        a: "Franconia (Würzburg, Nuremberg, Bamberg) leans wine and hearty sausage culture, Upper Bavaria beer culture and alpine cuisine. Feaststar filters both traditions by ingredient quality — not cliché.",
      },
    ],
  },
];

export const SEO_REGION_MAP = Object.fromEntries(SEO_REGIONS.map((r) => [r.slug, r]));
export const SEO_REGION_SLUGS = SEO_REGIONS.map((r) => r.slug);
